<template>
  <div>
    <div class="content-wrapper">
      <section class="content-header">
        <h1>
          Numérotation des factures
          <small>Administration</small>
        </h1>
        <ol class="breadcrumb">
          <li>
            <router-link :to="{ name: 'Dashboard'}">
              <i class="fa fa-dashboard"></i> Accueil
            </router-link>
          </li>
          <li class="active">Numérotation des factures</li>
        </ol>
      </section>

      <section class="content">
        <div class="row">
          <div class="col-md-7">
            <div class="box box-primary">
              <div class="overlay" v-if="occupe"><i class="fa fa-refresh fa-spin"></i></div>

              <div class="box-header with-border">
                <h3 class="box-title">Format des numéros</h3>
              </div>

              <div class="box-body">
                <p class="text-muted rappel">
                  Le numéro de facture est une <strong>référence interne</strong> : le logiciel
                  comptable fait foi pour le reste. Le format se change donc à tout moment, y
                  compris en cours d’année.
                </p>

                <div class="form-group" :class="{'has-error': erreurs.gabarit}">
                  <label for="gabarit">Gabarit</label>
                  <input type="text" id="gabarit" class="form-control" v-model.trim="format.gabarit">
                  <span class="help-block" v-if="erreurs.gabarit">{{ erreurs.gabarit }}</span>
                  <span class="help-block" v-else>
                    Trous disponibles :
                    <code v-for="trou in trous" :key="trou" class="trou"
                          @click="insererTrou(trou)">{{ trou }}</code>
                    — <code>{ANNEE}</code> et <code>{RANG}</code> sont obligatoires, chacun au
                    plus une fois.
                  </span>
                </div>

                <div class="row">
                  <div class="col-md-7">
                    <div class="form-group" :class="{'has-error': erreurs.prefixe}">
                      <label for="prefixe">Préfixe</label>
                      <input type="text" id="prefixe" class="form-control" v-model.trim="format.prefixe"
                             placeholder="peut rester vide">
                      <span class="help-block" v-if="erreurs.prefixe">{{ erreurs.prefixe }}</span>
                    </div>
                  </div>
                  <div class="col-md-5">
                    <div class="form-group" :class="{'has-error': erreurs.rangChiffres}">
                      <label for="rang">Chiffres du rang</label>
                      <input type="number" id="rang" class="form-control" min="1" max="8"
                             v-model="format.rangChiffres">
                      <span class="help-block" v-if="erreurs.rangChiffres">
                        {{ erreurs.rangChiffres }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="apercu" :class="{'apercu-invalide': erreurs.longueur}">
                  <div class="apercu-ligne">
                    <span class="apercu-intitule">Première facture de {{ anneeCourante }}</span>
                    <strong class="apercu-valeur">{{ apercu.exemple }}</strong>
                  </div>
                  <div class="apercu-ligne">
                    <span class="apercu-intitule">Le plus long possible</span>
                    <span class="apercu-valeur">
                      {{ apercu.plusLong }}
                      <small :class="erreurs.longueur ? 'text-red' : 'text-muted'">
                        {{ apercu.longueur }} / 32 caractères
                      </small>
                    </span>
                  </div>
                  <div class="apercu-ligne" v-if="communicationApercue">
                    <span class="apercu-intitule">Communication structurée</span>
                    <span class="apercu-valeur">{{ communicationApercue }}</span>
                  </div>
                </div>
                <p class="help-block" v-if="erreurs.longueur">{{ erreurs.longueur }}</p>
              </div>

              <div class="box-footer">
                <button type="button" class="btn btn-primary"
                        :disabled="occupe || enErreur || !modifie"
                        @click="enregistrer()">
                  <i class="fa fa-save"></i> Enregistrer
                </button>
                <button type="button" class="btn btn-default" :disabled="occupe || !modifie"
                        @click="reinitialiser()">
                  Annuler
                </button>
                <span class="text-muted etat" v-if="!modifie && !occupe">
                  Format en vigueur.
                </span>
              </div>
            </div>
          </div>

          <div class="col-md-5">
            <div class="box box-warning" v-if="avertissements.length">
              <div class="box-header with-border">
                <h3 class="box-title"><i class="fa fa-warning"></i> À vérifier</h3>
              </div>
              <div class="box-body">
                <p v-for="(message, rang) in avertissements" :key="rang" class="avertissement">
                  {{ message }}
                </p>
              </div>
            </div>

            <div class="box box-default">
              <div class="box-header with-border">
                <h3 class="box-title">Ce que ce changement ne fait pas</h3>
              </div>
              <div class="box-body text-muted petit">
                <p>
                  Les factures déjà émises <strong>gardent leur numéro</strong> : il engage un
                  document qui est parti.
                </p>
                <p>
                  Changer de préfixe en cours d’année <strong>fait repartir le compteur à 1</strong>,
                  la nouvelle série n’étant pas reconnue comme la suite de l’ancienne. Élargir le
                  nombre de chiffres, en revanche, poursuit la série.
                </p>
                <p>
                  La communication structurée se construit sur l’<strong>année et le rang</strong>,
                  sans le préfixe. Deux séries de préfixes différents partageant une année et un
                  rang portent donc la <strong>même</strong> communication : deux virements s’y
                  rapprocheraient de la même facture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script type="text/babel">
  import * as depot from '../../utils/facture/depot'
  import {erreursDeFormat, apercuFormat, formatNumerotation,
    definirFormatNumerotation} from '../../utils/facture/numerotation'

  export default {
    name: 'ParametresFacturation',
    data () {
      return {
        format: {gabarit: '', prefixe: '', rangChiffres: 4},
        initial: null,
        avertissements: [],
        // Les refus du serveur, par champ : ils priment sur les nôtres, qui ne
        // sont là que pour éviter l'aller-retour.
        erreursServeur: {},
        occupe: false,
        trous: ['{PREFIXE}', '{ANNEE}', '{RANG}']
      }
    },
    computed: {
      anneeCourante () {
        return new Date().getFullYear()
      },
      erreurs () {
        return Object.assign({}, erreursDeFormat(this.format), this.erreursServeur)
      },
      enErreur () {
        return Object.keys(this.erreurs).length > 0
      },
      apercu () {
        if (Object.keys(erreursDeFormat(this.format)).length) {
          return {exemple: '—', plusLong: '—', longueur: 0, communication: ''}
        }
        return apercuFormat(this.format, this.anneeCourante)
      },
      communicationApercue () {
        return this.apercu.communication || ''
      },
      modifie () {
        return JSON.stringify(this.format) !== JSON.stringify(this.initial)
      }
    },
    mounted () {
      this.charger()
    },
    methods: {
      charger () {
        const composant = this
        this.occupe = true
        depot.lireParametres().then(function (parametres) {
          composant.occupe = false
          composant.appliquer(parametres.numerotation || formatNumerotation())
        }, function (erreur) {
          composant.occupe = false
          // Sans les paramètres, on montre au moins le format en vigueur.
          composant.appliquer(formatNumerotation())
          composant.$noty.error(composant.message(erreur, 'Paramètres illisibles'))
        })
      },

      appliquer (numerotation) {
        this.format = {
          gabarit: numerotation.gabarit,
          prefixe: numerotation.prefixe,
          rangChiffres: numerotation.rangChiffres
        }
        this.initial = Object.assign({}, this.format)
        this.erreursServeur = {}
      },

      reinitialiser () {
        this.format = Object.assign({}, this.initial)
        this.erreursServeur = {}
      },

      insererTrou (trou) {
        if (this.format.gabarit.indexOf(trou) !== -1) return
        this.format.gabarit += trou
      },

      enregistrer () {
        const composant = this
        this.occupe = true
        this.erreursServeur = {}
        this.avertissements = []

        const numerotation = {
          gabarit: this.format.gabarit,
          prefixe: this.format.prefixe,
          rangChiffres: parseInt(this.format.rangChiffres, 10)
        }

        depot.majParametres({numerotation: numerotation}).then(function (parametres) {
          composant.occupe = false
          composant.appliquer(parametres.numerotation || numerotation)
          // Le format en vigueur pour le reste de l'application suit tout de suite.
          definirFormatNumerotation(parametres.numerotation || numerotation)
          composant.avertissements = parametres.avertissements || []
          composant.$noty.success('Format enregistré')
        }, function (erreur) {
          composant.occupe = false
          const corps = (erreur && erreur.data) || {}
          if (erreur && erreur.status === 422 && corps.erreurs) {
            composant.erreursServeur = corps.erreurs
            composant.$noty.error('Format refusé')
            return
          }
          if (erreur && erreur.status === 403) {
            composant.$noty.error('Droits insuffisants : réservé aux administrateurs.')
            return
          }
          composant.$noty.error(composant.message(erreur, 'Enregistrement impossible'))
        })
      },

      message (erreur, defaut) {
        const corps = (erreur && erreur.data) || {}
        const texte = corps.message || corps.error
        return texte ? defaut + ' : ' + String(texte).slice(0, 180)
          : defaut + ' (' + ((erreur && erreur.status) || '?') + ')'
      }
    }
  }
</script>

<style scoped>
  .rappel {
    margin-bottom: 18px;
    font-size: 12px;
  }

  .trou {
    cursor: pointer;
    margin-right: 4px;
  }

  .apercu {
    margin-top: 6px;
    padding: 12px 14px;
    background: #f4f8fb;
    border-left: 4px solid #3c8dbc;
  }

  .apercu-invalide {
    background: #fdf3f2;
    border-left-color: #dd4b39;
  }

  .apercu-ligne {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 3px 0;
  }

  .apercu-intitule {
    font-size: 12px;
    color: #777;
  }

  .apercu-valeur {
    font-family: monospace;
  }

  .etat {
    margin-left: 10px;
    font-size: 12px;
  }

  .avertissement {
    font-size: 12px;
    line-height: 1.55;
    margin-bottom: 10px;
  }

  .petit {
    font-size: 12px;
    line-height: 1.55;
  }
</style>



// WEBPACK FOOTER //
// src/components/Administration/ParametresFacturation.vue